"use client";

/**
 * Tracks page views for GA4 and Amplitude on route changes.
 *
 * - GA4: tracks all routes (public, auth, app).
 * - Amplitude: tracks all pages including public marketing pages,
 *   with UTM/campaign context for conversion analysis.
 */

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";
import { trackEvent } from "@/lib/analytics/amplitude";
import { trackPageView } from "@/lib/analytics/gtag";

/** Routes where Amplitude page_view events should fire. */
const AMPLITUDE_TRACKED_PREFIXES = [
  // Auth pages
  "/login",
  "/signup",
  "/dev-login",
];

/** Public marketing pages that should be tracked with UTM context. */
const PUBLIC_MARKETING_PAGES = [
  "/",
  "/blog",
  "/changelog",
  "/download",
  "/features",
  "/pricing",
  "/product",
  "/how-to",
  "/getting-started",
  "/privacy-policy",
  "/terms-of-service",
  "/seo",
  "/search",
  "/seeds",
  "/about",
  "/alternatives",
  "/intellectual-property",
  "/refund-policy",
  "/contact",
];

function isPublicMarketingPath(pathname: string): boolean {
  return PUBLIC_MARKETING_PAGES.some((p) =>
    p === "/" ? pathname === "/" : pathname.startsWith(p),
  );
}

function shouldTrackAmplitude(pathname: string): boolean {
  // All authenticated app pages (dashboard, projects, settings, etc.)
  if (
    !pathname.startsWith("/login") &&
    !pathname.startsWith("/signup") &&
    !pathname.startsWith("/dev-login") &&
    !isPublicMarketingPath(pathname)
  ) {
    return true;
  }
  // Explicitly allowlisted auth pages
  return AMPLITUDE_TRACKED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function getUtmProps(
  searchParams: URLSearchParams,
): Record<string, string | undefined> {
  return {
    utm_source: searchParams.get("utm_source") ?? undefined,
    utm_medium: searchParams.get("utm_medium") ?? undefined,
    utm_campaign: searchParams.get("utm_campaign") ?? undefined,
    utm_content: searchParams.get("utm_content") ?? undefined,
    utm_term: searchParams.get("utm_term") ?? undefined,
  };
}

export function AnalyticsTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname) return;

    const timeoutId = window.setTimeout(() => {
      const pageTitle = document.title || "VidSeeds.ai";
      const fullPath =
        pathname +
        (typeof window !== "undefined" ? window.location.search : "");

      if (fullPath !== lastTrackedPath.current) {
        lastTrackedPath.current = fullPath;

        // GA4 page view — always tracked (Search Console, ads, SEO)
        trackPageView(fullPath, pageTitle);

        // Amplitude page view — all pages including public marketing
        if (shouldTrackAmplitude(fullPath)) {
          const utmProps = getUtmProps(searchParams);
          const referrer =
            typeof document !== "undefined" ? document.referrer : undefined;
          trackEvent("page_view", {
            path: fullPath,
            page_title: pageTitle,
            referrer,
            ...utmProps,
          });
        }
      }
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [pathname, searchParams]);

  return null;
}
